import { useMemo, useState } from 'react';
import { Input, Modal, Radio, Select } from 'antd';
import { Formik } from 'formik';

import FormField from 'components/common/form-field/FormField';
import { addErrorMessage } from 'components/common/form-field/pure-field/FormikEnhance';
import FormikFieldTouched from 'components/common/formik-field-touched/FormikFieldTouched';
import { checkDataSourceName } from 'lib/apis/data-source';
import { CHARACTER_SIZE } from 'lib/global';
import useI18n from 'lib/hooks/useI18n';
import { FormError } from 'lib/types/error';

import { DataModalProps } from './types';
import { DATA_SOURCE_MODE, FIELD_DATA_TYPE, getDataSourceModeMap, getFieldDataTypeMap } from './util';

const DataModal: React.FC<DataModalProps> = ({
  modalVisible,
  initialValue,
  handleSubmit,
  handleCloseModal,
  title,
  disabledChooseFieldDataType,
}) => {
  const { t } = useI18n();

  const fieldDataTypeMap = getFieldDataTypeMap();
  const dataSourceModeMap = getDataSourceModeMap();
  const [modeVisible, setModeVisible] = useState(() => initialValue?.fieldDataType === FIELD_DATA_TYPE.DROPDOWN);

  const isAdd = useMemo(() => {
    return !initialValue?.objectId;
  }, [initialValue]);
  const validate = async values => {
    const errors = {} as FormError;
    if (!values.name || !values.name.trim()) {
      errors.name = addErrorMessage(errors.name, t('validate.dataSource.noRepeatDataSourceName'));
    } else if (
      values.name.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH ||
      values.name.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH
    ) {
      errors.name = addErrorMessage(
        errors.name,
        t('validate.dataSource.dataSourceName', {
          min: CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH,
          max: CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH,
        }),
      );
    } else {
      const { isExist } = await checkDataSourceName(values.name, values.objectId);
      if (isExist) {
        errors.name = addErrorMessage(errors.name, t('placeholder.default.nameExist'));
      }
    }
    if (!values.fieldDataType || !values.fieldDataType.trim()) {
      errors.fieldDataType = addErrorMessage(errors.fieldDataType, t('validate.dataSource.noEmptyForDataSourceType'));
    }
    setModeVisible(values?.fieldDataType === FIELD_DATA_TYPE.DROPDOWN);
    return errors;
  };
  return (
    <Formik
      initialValues={
        initialValue ? initialValue : { name: undefined, fieldDataType: undefined, mode: DATA_SOURCE_MODE.CUSTOM }
      }
      onSubmit={handleSubmit}
      validate={validate}
    >
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          title={title}
          okText={t('global.modal.okText')}
          cancelText={t('global.modal.cancelText')}
          open={modalVisible}
          onOk={() => handleSubmit()}
          onCancel={handleCloseModal}
        >
          <FormikFieldTouched />
          <FormField name="name" label={t('global.name')} required>
            {({ field }) => (
              <Input
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH}
                {...field}
                placeholder={t('placeholder.dataSource.inputNameLong', {
                  min: CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH,
                  max: CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH,
                })}
              />
            )}
          </FormField>
          <FormField name="fieldDataType" label={t('global.type')} required>
            {({ field }) => (
              <Select
                {...field}
                placeholder={t('placeholder.default.select')}
                disabled={disabledChooseFieldDataType || !isAdd}
                options={Object.keys(fieldDataTypeMap).map(key => ({ value: key, label: fieldDataTypeMap[key] }))}
              />
            )}
          </FormField>
          {modeVisible ? (
            <FormField name="mode" label={t('pages.dataSource.mode.title')} required>
              {({ field }) => (
                <Radio.Group
                  {...field}
                  disabled={!isAdd}
                  options={Object.keys(dataSourceModeMap).map(key => ({ value: key, label: dataSourceModeMap[key] }))}
                />
              )}
            </FormField>
          ) : null}
        </Modal>
      )}
    </Formik>
  );
};

export default DataModal;
